import React, { useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import DetailCard from '../../components/detailCard/DetailCard';
import CustomTable from '../../components/customtable/CustomTable';
import { reportNameFormat } from '../../utils/commonUtils';
import toastCenter from '../../utils/toastCenter';
import APPCONSTANTS from '../../constants/appConstants';
import SKFilterFields from './SKFilterFields';
import { IDateRangeFormSlice } from './DateRangeFields';
import { IRegionFiltersFormValues } from './RegionFilterFields';
import Loader from '../../components/loader/Loader';
import { skReportListSelector, skReportLoadingSelector, skReportTotalSelector } from '../../store/report/selectors';
import ERRORS from '../../constants/error';
import { downloadSKReportExcel } from '../../services/reportAPI';
import { fetchSKReportViewRequest } from '../../store/report/actions';
import { ISKReportViewRequestPayload } from '../../store/report/types';

export interface ISKFiltersFormValues extends IDateRangeFormSlice, IRegionFiltersFormValues {}

export interface ISKReportFormState {
  skreport: ISKFiltersFormValues;
}

export const validateSKReportFilters = (formValues: ISKFiltersFormValues): boolean =>
  Boolean(formValues?.fromDate && formValues?.toDate);

const buildSKReportPayload = (
  formValues: ISKFiltersFormValues,
  pagination?: { skip: number; limit: number }
): ISKReportViewRequestPayload | null => {
  const { fromDate, toDate, division, district, upazila } = formValues;

  if (!validateSKReportFilters(formValues)) {
    return null;
  }

  return {
    startDate: moment(fromDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
    endDate: moment(toDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
    ...(division?.name && { division: division.name }),
    ...(district?.name && { district: district.name }),
    ...(upazila?.name && { upazila: upazila.name }),
    skip: pagination?.skip,
    limit: pagination?.limit
  };
};

const SKReport = (): React.ReactElement => {
  const dispatch = useDispatch();
  const reportRows = useSelector(skReportListSelector);
  const isReportLoading = useSelector(skReportLoadingSelector);
  const totalCount = useSelector(skReportTotalSelector);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [page, setPage] = useState(APPCONSTANTS.NUMBER.ONE);
  const [rowsPerPage, setRowsPerPage] = useState(APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE);
  const initialValues = useMemo(
    () => ({
      skreport: {
        fromDate: undefined,
        toDate: undefined,
        division: null,
        district: null,
        upazila: null
      }
    }),
    []
  );

  const handleDownloadReport = async (values: ISKReportFormState) => {
    if (isDownloadLoading) {
      return;
    }

    const payload = buildSKReportPayload(values?.skreport || ({} as ISKFiltersFormValues));
    if (!payload) {
      toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.SK_REPORT_FILTER_VALIDATION_ERROR);
      return;
    }

    try {
      setIsDownloadLoading(true);
      await downloadSKReportExcel(payload);
      toastCenter.success(APPCONSTANTS.SUCCESS, APPCONSTANTS.REPORT_DOWNLOAD_SUCCESS);
    } catch (error) {
      if ((error as Error)?.message === ERRORS.NETWORK_ERROR.message) {
        toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
      } else {
        toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.DOWNLOAD_FAIL_MSG);
      }
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const fetchPaginatedSKReport = (formValues: ISKFiltersFormValues, nextPage: number, nextRowsPerPage: number) => {
    const payload = buildSKReportPayload(formValues, {
      skip: (nextPage - 1) * nextRowsPerPage,
      limit: nextRowsPerPage
    });

    if (!payload) {
      return;
    }

    dispatch(
      fetchSKReportViewRequest({
        payload,
        failureCb: (error) => {
          if (error.message === ERRORS.NETWORK_ERROR.message) {
            toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
          } else {
            toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.SK_REPORT_FETCH_ERROR);
          }
        }
      })
    );
  };

  return (
    <Form<ISKReportFormState>
      onSubmit={() => undefined}
      initialValues={initialValues}
      render={({ form }) => (
        <div className='row'>
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <SKFilterFields
                form={form}
                name='skreport'
                values={values || {}}
                rowsPerPage={rowsPerPage}
                onFetchSKReport={(formValues, nextPage, nextRowsPerPage) => {
                  setPage(nextPage);
                  fetchPaginatedSKReport(formValues, nextPage, nextRowsPerPage);
                }}
              />
            )}
          </FormSpy>
          <div className='col'>
            <DetailCard
              header={reportNameFormat(APPCONSTANTS.REPORT_LIST.SK_REPORT)}
              buttonLabel={isDownloadLoading ? 'Exporting...' : 'Export'}
              onButtonClick={() => handleDownloadReport(form.getState().values)}
              isDownload={true}
              isDisabled={isDownloadLoading}
            >
              {isReportLoading ? (
                <Loader />
              ) : (
                <CustomTable
                  rowData={reportRows}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={totalCount}
                  handlePageChange={(pageNo: number, nextRowsPerPage?: number) => {
                    const updatedRowsPerPage = nextRowsPerPage || rowsPerPage;
                    setPage(pageNo);
                    setRowsPerPage(updatedRowsPerPage);
                    const values = form.getState().values;
                    fetchPaginatedSKReport(values?.skreport ?? {}, pageNo, updatedRowsPerPage);
                  }}
                  columnsDef={[
                    { id: 1, name: 'providerId', label: 'provider id' },
                    { id: 2, name: 'providerName', label: 'Provider Name' },
                    { id: 3, name: 'userRole', label: 'User Role' },
                    { id: 4, name: 'unionName', label: 'Union' },
                    { id: 5, name: 'ancServiceCount', label: 'ANC Service' },
                    {
                      id: 6,
                      name: 'anc3OrMoreCount',
                      label: 'anc 3 or more from BRAC (Unique Pregnant)'
                    },
                    { id: 7, name: 'pncServiceCount', label: 'PNC Service' },
                    {
                      id: 8,
                      name: 'pncWithin72hrs',
                      label: 'pnc within 72hrs'
                    },
                    {
                      id: 9,
                      name: 'pncAfter72hrs',
                      label: 'PNC after 72 hrs'
                    },
                    { id: 10, name: 'totalPregnantRegistered', label: 'total pregnant registered' },
                    { id: 11, name: 'ncdCount', label: 'NCD Service' },
                    { id: 12, name: 'eyeTestGlassSold', label: 'eyetest glass sold' }
                  ]}
                  isDelete={false}
                  isEdit={false}
                  isView={false}
                  isDownload={false}
                />
              )}
            </DetailCard>
          </div>
        </div>
      )}
    />
  );
};

export default SKReport;
